import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RestRequestService } from '@app/restApi/rest-request.service';
import {
  SubmittalPackageListingItem,
  SubmittalPackageFilterParams,
  SubmittalRegistry,
  SubmittalRegistryFilterParams,
  SubmittalRegistryPayloadDto,
  SubmittalPackageCreateUpdateReq,
  SubmittalPackageResp,
  SubmittalRegistryBulkAssignDataDto,
  SubmittalRegistryPartialUpdateDto,
  SubmittalPackagePatchPayloadDto,
  SUBMITTAL_PACKAGE_STATUS,
} from '@app/store/submittals/submittals.types';
import {
  REST_BASE,
  REST_PROJECTS,
  REST_SUBMITTAL_PACKAGES,
  REST_SUBMITTAL_REGISTRY_ENTRIES,
} from '@app/restApi/RestRoutes';
import { SUBMITTALS_PER_PAGE } from '@app/store/submittals/submittals.reducer';

@Injectable({
  providedIn: 'root',
})
export class SubmittalsApiService {
  rest = inject(RestRequestService);

  private getLogQueryParams(filters?: SubmittalPackageFilterParams): Record<string, any> {
    const params: any = {};

    params.perPage = SUBMITTALS_PER_PAGE;
    params.page = filters?.page ?? 1;
    if (filters?.status) params['status[]'] = filters.status;
    if (filters?.search) params.search = filters.search;
    if (filters?.sortBy) params.sortBy = filters.sortBy;
    if (filters?.sortDirection) params.sortDirection = filters.sortDirection;

    return params;
  }

  private getRegistryQueryParams(filters?: SubmittalRegistryFilterParams): Record<string, any> {
    const params: any = {};

    params.perPage = SUBMITTALS_PER_PAGE;
    params.page = filters?.page ?? 1;
    if (filters?.status !== undefined) params['status[]'] = filters.status;
    if (filters?.contractor) params.contractor = filters.contractor;
    if (filters?.search) params.search = filters.search;
    if (filters?.sortBy) params.sortBy = filters.sortBy;
    if (filters?.sortDirection) params.sortDirection = filters.sortDirection;

    return params;
  }

  // ----------- Submittal Registry Entries -----------

  loadSubmittalRegistries(
    projectId: number,
    filters?: SubmittalRegistryFilterParams,
  ): Observable<SubmittalRegistry[]> {
    const params = this.getRegistryQueryParams(filters);
    return this.rest.getWithObservable(
      `${REST_PROJECTS}/${projectId}/submittal-registry-entries`,
      {},
      params,
    );
  }

  createSubmittalRegistry(body: SubmittalRegistryPayloadDto): Observable<SubmittalRegistry> {
    return this.rest.postWithObservable(`${REST_SUBMITTAL_REGISTRY_ENTRIES}`, body, {});
  }

  updateSubmittalRegistryEntry(
    id: number,
    registryEntry: SubmittalRegistryPayloadDto,
  ): Observable<SubmittalRegistry> {
    return this.rest.putWithObservable(`${REST_SUBMITTAL_REGISTRY_ENTRIES}/${id}`, registryEntry);
  }

  updateSubmittalRegistryEntryPatch(
    id: number,
    registryEntry: Partial<SubmittalRegistryPartialUpdateDto>,
  ): Observable<SubmittalRegistry> {
    // all fields are required to ensure nullish values are NOT sent to the API
    // E.g. status wasn't updated, so we don't want to send it at all
    const payload: Required<SubmittalRegistryPartialUpdateDto> = {
      note: registryEntry.note ?? undefined,
      status: registryEntry.status || undefined,
      is_locked: registryEntry.is_locked,
    };
    return this.rest.patchWithObservable(`${REST_SUBMITTAL_REGISTRY_ENTRIES}/${id}`, payload);
  }

  getNextRegistryId(projectId: number): Observable<{ next_id: string }> {
    return this.rest.getWithObservable(`${REST_PROJECTS}/${projectId}/next-id/submittals`, {});
  }

  validateNextRegistryId(projectId: number, proposedId: string): Observable<{ message: string }> {
    return this.rest.postWithObservable(
      `${REST_PROJECTS}/${projectId}/next-id/submittals/validate/${proposedId}`,
      {},
    );
  }

  createRevisionForRegistryItems(registryId: number): Observable<SubmittalRegistry> {
    return this.rest.postWithObservable(
      `${REST_SUBMITTAL_REGISTRY_ENTRIES}/${registryId}/create-revision`,
      {},
    );
  }

  deleteSubmittalRegistryItem(registryId: number): Observable<void> {
    return this.rest.delWithObservable(`${REST_SUBMITTAL_REGISTRY_ENTRIES}/${registryId}`, {});
  }

  bulkAssignRegistryOwner(data: SubmittalRegistryBulkAssignDataDto): Observable<void> {
    return this.rest.patchWithObservable(`${REST_SUBMITTAL_REGISTRY_ENTRIES}/bulk-assign`, data);
  }

  // ----------- Submittal Packages (Submittal Log) -----------

  loadSubmittalPackages(
    projectId: number,
    filters?: SubmittalPackageFilterParams,
  ): Observable<SubmittalPackageListingItem[]> {
    const params = this.getLogQueryParams(filters);
    return this.rest.getWithObservable(
      `${REST_PROJECTS}/${projectId}/submittal-packages`,
      {},
      params,
    );
  }

  loadPackageDetails(packageId: number): Observable<SubmittalPackageResp> {
    return this.rest.getWithObservable(`${REST_SUBMITTAL_PACKAGES}/${packageId}`, {});
  }

  createSubmittalPackage(body: SubmittalPackageCreateUpdateReq): Observable<SubmittalPackageResp> {
    return this.rest.postWithObservable(`${REST_BASE}submittal-packages`, body, {});
  }

  deletePackage(packageId: number): Observable<void> {
    return this.rest.delWithObservable(`${REST_SUBMITTAL_PACKAGES}/${packageId}`, {});
  }

  updateSubmittalPackage(
    submittalPackage: SubmittalPackageCreateUpdateReq,
  ): Observable<SubmittalPackageResp> {
    return this.rest.putWithObservable(
      `${REST_BASE}submittal-packages/${submittalPackage.id}`,
      submittalPackage,
      {},
    );
  }

  updateSubmittalPackagePatch(
    id: number,
    submittalPackage: Partial<SubmittalPackagePatchPayloadDto>,
  ): Observable<SubmittalPackageResp> {
    return this.rest.patchWithObservable(
      `${REST_SUBMITTAL_PACKAGES}/${id}/patch`,
      submittalPackage,
    );
  }

  updatePackageStatus(
    packageId: number,
    newStatus: SUBMITTAL_PACKAGE_STATUS,
  ): Observable<SubmittalPackageResp> {
    return this.rest.patchWithObservable(`${REST_SUBMITTAL_PACKAGES}/${packageId}/update-status`, {
      status: newStatus,
    });
  }

  requestSubmittalsFromOwners(packageId: number): Observable<SubmittalPackageResp> {
    return this.rest.patchWithObservable(
      `${REST_SUBMITTAL_PACKAGES}/${packageId}/request-submittals-from-owners`,
      {},
    );
  }

  cancelRequestSubmittalsFromOwners(packageId: number): Observable<SubmittalPackageResp> {
    return this.rest.patchWithObservable(
      `${REST_SUBMITTAL_PACKAGES}/${packageId}/request-submittals-from-owners/cancel`,
      {},
    );
  }

  requestApproval(packageId: number): Observable<SubmittalPackageResp> {
    return this.rest.patchWithObservable(
      `${REST_SUBMITTAL_PACKAGES}/${packageId}/request-approval`,
      {},
    );
  }

  cancelRequestApproval(packageId: number): Observable<SubmittalPackageResp> {
    return this.rest.patchWithObservable(
      `${REST_SUBMITTAL_PACKAGES}/${packageId}/request-approval/cancel`,
      {},
    );
  }

  markAsComplete(packageId: number): Observable<SubmittalPackageResp> {
    return this.rest.patchWithObservable(
      `${REST_SUBMITTAL_PACKAGES}/${packageId}/send-approval`,
      {},
    );
  }

  markAsReady(packageId: number): Observable<SubmittalPackageResp> {
    return this.rest.patchWithObservable(
      `${REST_SUBMITTAL_PACKAGES}/${packageId}/send-submittals-from-owner`,
      {},
    );
  }

  createRevisionForPackage(packageId: number): Observable<SubmittalPackageResp> {
    return this.rest.postWithObservable(
      `${REST_SUBMITTAL_PACKAGES}/${packageId}/create-revision`,
      {},
    );
  }

  submitPackageToOwners(packageId: number): Observable<SubmittalPackageResp> {
    return this.rest.patchWithObservable(
      `${REST_SUBMITTAL_PACKAGES}/${packageId}/submit-to-owners`,
      {},
    );
  }

  cancelSubmitPackageToOwners(packageId: number): Observable<SubmittalPackageResp> {
    return this.rest.patchWithObservable(
      `${REST_SUBMITTAL_PACKAGES}/${packageId}/submit-to-owners/cancel`,
      {},
    );
  }
}
